import { useTranslation } from "react-i18next";

import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { PredictionInputErrorState } from "#modules/quoteRequestLines/views/QuoteRequestLineRightSection/PredictionInputErrorState";
import { MaterialGradeType } from "shared";

import { ConsumableCostDisplay } from "../../ConsumableCostDisplay";
import { AnalysisErrorState } from "./AnalysisErrorState";
import { AnalyzingState } from "./AnalyzingState";
import { ChildrenPredictionErrorState } from "./ChildrenPredictionErrorState";
import { ChildrenPredictionMissingState } from "./ChildrenPredictionMissingState";
import { LowConfidenceState } from "./LowConfidenceState";
import { NoPredictionState } from "./NoPredictionState";
import { PredictedState } from "./PredictedState";
import { PredictingState } from "./PredictingState";
import { PredictionErrorState } from "./PredictionErrorState";
import { WaitingForDataState } from "./WaitingForDataState";

type Props = {
  currentState: string;
  quoteRequestLine: QuoteRequestLineDto;
  materialGrade: MaterialGradeType | null;
  quantity: number;
  openDetailsPanel: () => void;
  onRetryPrediction: () => void;
  onRetryAnalysis: () => void;
  onOpenThumbnailOverlay: () => void;
  hasChildren: boolean;
};

export const StateRenderer = ({
  currentState,
  quoteRequestLine,
  materialGrade,
  quantity,
  openDetailsPanel,
  onRetryPrediction,
  onRetryAnalysis,
  onOpenThumbnailOverlay,
  hasChildren,
}: Props) => {
  const { t } = useTranslation();

  const sharedProps = {
    quoteRequestLine,
    materialGrade,
    quantity,
    openDetailsPanel,
    onRetryPrediction,
    onRetryAnalysis,
  };

  switch (currentState) {
    case "consumable":
      return <ConsumableCostDisplay quoteRequestLine={quoteRequestLine} />;

    case "analyzingPart":
      return <AnalyzingState {...sharedProps} text={t("qrl.price.analyzing")} />;

    case "analyzingAssembly":
      return <AnalyzingState {...sharedProps} text={t("qrl.assembly.splitting")} />;

    case "analysisError":
      return (
        <AnalysisErrorState
          {...sharedProps}
          errorText={t("qrl.analyze.error")}
          retryText={t("qrl.analyze.relaunch")}
          secondaryRetryText={t("qrl.price.lowConfidence.retryLabel")}
        />
      );

    case "waitingForData":
      return <WaitingForDataState />;

    case "childrenPredictionError":
      return <ChildrenPredictionErrorState />;

    case "childrenPredictionMissing":
      return <ChildrenPredictionMissingState />;

    case "nesting":
    case "predicting":
      return <PredictingState />;

    case "predictionInputError":
      return (
        <PredictionInputErrorState
          onClick={onOpenThumbnailOverlay}
          label={t("qrl.analyze.predictionInputError.label")}
          cta={t("qrl.analyze.predictionInputError.cta")}
        />
      );

    case "partVolumeOutOfBounds":
      return (
        <PredictionInputErrorState
          onClick={onOpenThumbnailOverlay}
          label={t("qrl.analyze.partVolumeOutOfBounds.label")}
          cta={t("qrl.analyze.partVolumeOutOfBounds.cta")}
        />
      );

    case "predictionError":
      return (
        <PredictionErrorState
          {...sharedProps}
          errorText={t("qrl.analyze.predictionError")}
          retryText="Relancer la prédiction"
          secondaryRetryText={t("qrl.price.lowConfidence.retryLabel")}
        />
      );

    case "noPrediction":
      return (
        <NoPredictionState
          {...sharedProps}
          errorText={t("qrl.price.noPrediction.message")}
          retryText={t("qrl.price.noPrediction.retryLabel")}
        />
      );

    case "lowConfidence":
      return (
        <LowConfidenceState
          {...sharedProps}
          errorText={t("qrl.price.lowConfidence.message")}
          retryText={t("qrl.price.lowConfidence.retryLabel")}
        />
      );

    case "predicted":
      return (
        <PredictedState
          quoteRequestLine={quoteRequestLine}
          openDetailsPanel={openDetailsPanel}
          onRetryPrediction={onRetryPrediction}
          hasChildren={hasChildren}
        />
      );

    default:
      return null;
  }
};
